/**
 * Row reposition: move a whole repeated row (Figma `<key>#group`, see
 * Scene.rows) as one unit, and put it back.
 *
 * A row is not a node — the scene is flat and each member only carries
 * `groupId` — so moving one means writing the SAME delta into every member's
 * `sceneTransforms[el.id]` x/y. That is the store both the editor and the
 * export already read, so a row move needs no API, Lambda or draft change.
 *
 * Callers pass the design's FULL template scene (`design.scene`), never the
 * merged/visible one: a layer switched off inside the row (a row toggle, see
 * rowVisibility) is missing from the visible scene, and if only the visible
 * members moved, switching it back on would leave it behind at the old spot.
 */
import {Scene, SceneTransforms} from './types';

/** Ids of the elements belonging to `rowKey`, visible or not. */
export const rowMemberIds = (scene: Scene, rowKey: string): string[] =>
    scene.elements.filter((el) => el.groupId === rowKey).map((el) => el.id);

/** Shift every member of the row by (dx, dy) scene units, from its current
 *  override or, with none, from its own position. Returns a NEW object. */
export const moveRow = (
    transforms: SceneTransforms,
    scene: Scene,
    rowKey: string,
    dx: number,
    dy: number,
): SceneTransforms => {
    const next = {...transforms};
    for (const el of scene.elements) {
        if (el.groupId !== rowKey) continue;
        const current = transforms[el.id];
        next[el.id] = {...current, x: (current?.x ?? el.x) + dx, y: (current?.y ?? el.y) + dy};
    }
    return next;
};

/** Drop the x/y overrides of every member of the row. Everything else the user
 *  set on them (font size, box size, rotation, pan) is kept; an entry left with
 *  nothing in it is removed. Returns a NEW object. */
export const resetRow = (transforms: SceneTransforms, scene: Scene, rowKey: string): SceneTransforms => {
    const next = {...transforms};
    for (const id of rowMemberIds(scene, rowKey)) {
        if (!next[id]) continue;
        const {x, y, ...rest} = next[id];
        if (Object.keys(rest).length) next[id] = rest;
        else delete next[id];
    }
    return next;
};
